import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PhotoHoldCanvas } from '../photo-editor/PhotoHoldCanvas';
import { PhotoLineCanvas } from '../photo-editor/PhotoLineCanvas';
import { PhotoOverlayTools } from '../photo-editor/PhotoOverlayTools';
import type { PhotoHoldsController } from '../photo-editor/usePhotoHolds';
import type { PhotoLineController } from '../photo-editor/usePhotoLine';
import { HoldRolesLegendDialog } from '../HoldRolesLegendDialog';
import { PHOTO_HOLD_ROLE_CYCLE, photoHoldRole } from '../photo-hold-roles';
import { forcedHandIndex, photoHandsOf, swapPhotoHand } from '../photo-circuit';
import { otherHand } from '../board-grid/roles';
import type { HoldHandControl } from './HoldHandButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { ClimbPhotoOverlayActions, type ClimbPhotoCircuitControls } from './ClimbPhotoOverlayActions';
import { CircuitOverlayPanel } from './CircuitOverlayPanel';
import { PhotoHoldBar } from './PhotoHoldBar';
import { HoldFilterPanel } from './HoldFilterPanel';
import type { HoldFilter } from './hold-filter';
import { LiveScanOverlay } from './LiveScanOverlay';
import type { LiveScanState } from './useLiveScan';
import { LinePopover } from './LinePopover';
import { MissingHint } from './MissingHint';
import { PhotoExpandButton } from './PhotoExpandButton';
import { DRAWING_SURFACE_ATTRIBUTE } from './useClearSelectionOutside';
import { editorPhotoFrameStyle } from './photo-height';
import type { PhotoExpansion } from './useExpandablePhoto';
import { requirementAnchor } from './requirements';
import type { ClimbPhoto } from './useClimbPhoto';
import type { Hold } from '../../../types/climb';
import type { BoardHand } from '../../../types/device';

interface PhotoClimbSurfaceProps {
  photo: ClimbPhoto;
  /**
   * The two editors, made by the page rather than here. The page needs the one
   * in use at save time — its zoom says which part of the photo is on screen,
   * which is the crop — and a controller made in here would be out of its reach.
   */
  holdEditor: PhotoHoldsController;
  lineEditor: PhotoLineController;
  /** A route is drawn as one line; a boulder is circled hold by hold. */
  drawsLine: boolean;
  /** Offer the corridor — off on rock, where the line is the whole topo. */
  allowCorridor: boolean;
  /**
   * What is still missing *from the drawing*, named. Written on the picture,
   * which is the only place those answers can be given — see `MissingHint`.
   *
   * Advice here, never a refusal: a climb on a photo may go up undrawn, and the
   * save asks about it once rather than holding it back (see `isDrawingAdvice`).
   */
  missing: string[];
  onChangePhoto: () => void;
  /**
   * Whether the photo is a scanned wall and the climb is chosen off the holds
   * the scan found rather than drawn (see `PhotoClimbEditor`). The wall is then
   * dimmed and the climb's holds lit on it, as on the gym panel's scans page —
   * among dozens of placeholders, a ring round a hold on a busy photograph does
   * not say which are in — and the legend's (i) describes that gesture instead
   * of drawing.
   */
  scanned?: boolean;
  /**
   * Circuit mode and everything that hangs off it — the switch in the corner of
   * the photo, the list of holds over it, and the hands that follow from their
   * order. Absent where a climb can have no circuit: a route is one line from the
   * ground to the anchor, with nothing to put in sequence.
   */
  circuit?: PhotoCircuitControls;
  /**
   * Opening a landscape photo out to the height of the screen. The page holds
   * it, because opened out the picture and the fields are laid out together as
   * one column of the screen's height — this then fills whatever the column
   * leaves it. See `useExpandablePhoto`.
   */
  expansion?: PhotoExpansion;
  /**
   * The photo being read for holds right now (`useLiveScan`), and giving up
   * on that: drawn over the picture until its holds are in.
   */
  liveScan?: { state: LiveScanState; onSkip: () => void } | null;
  /**
   * Which of a scanned wall's holds are offered — the button on the bar and the
   * panel it opens (`HoldFilterPanel`). Absent off a scanned wall.
   */
  holdFilter?: PhotoHoldFilterControls;
  /**
   * Where the bar of roles and the circuit's list go when they are not on or
   * under the picture: the column beside it, on a phone held sideways (see
   * `ClimbEditorSplit`). Portalled, so their state stays here with the drawing
   * they act on. Absent — or not mounted yet — they stay where they always are.
   */
  controlsTarget?: HTMLElement | null;
}

/** The filter of a scanned wall's holds, as the page holds it. */
export interface PhotoHoldFilterControls {
  filter: HoldFilter;
  onChange: (filter: HoldFilter) => void;
  onConfidenceCommit: (minScore: number | null) => void;
  confidenceFloor: number | null;
  sizeRange: { min: number; max: number } | null;
  hidden: number;
  total: number;
  saving?: boolean;
}

/**
 * What the picture keeps back, under it, for the bar of hold roles: the bar
 * (2.75rem) and its padding. Counted against the photo's height cap so the bar
 * is on screen with the photo rather than under the fold.
 */
const HOLD_BAR_ROOM = '3.75rem';

/** Circuit mode as the page holds it, for the surface to draw the controls of. */
export interface PhotoCircuitControls extends ClimbPhotoCircuitControls {
  forcedHands: boolean;
  onForcedHandsChange: (on: boolean) => void;
  onStartingHandChange: (hand: BoardHand) => void;
  /** The holds with their hands on, which is what the list reads back. */
  holds: Hold[];
  onHoldsChange: (holds: Hold[]) => void;
}

/**
 * The climb being drawn on its photo.
 *
 * Both halves come from `components/climbs/photo-editor`, shared with the gym
 * panel's scans page; this supplies the member app's own copy for them and the
 * overlay that changes the picture underneath.
 *
 * Everything is on the picture or right under it. What acts on the mark in
 * hand — and, for the holds, what the next mark will be — is a bar: under the
 * picture for the holds (`PhotoHoldBar`, #320 — on a scanned wall the bar
 * covered the very placeholders it acts on), along its bottom for a route's
 * line (`LinePopover`); and what acts on the drawing as a whole —
 * taking a mark back, wiping the lot, asking what any of it means — is in the
 * corner of the picture it acts on. A row of controls at the top would be
 * scrolled away exactly when the drawing gets interesting, and one at the foot
 * of the screen would come apart from the picture it is about.
 *
 * A press on the circle already chosen steps its **size** here. On a scan it
 * steps the role instead — there the analysis guessed the colour and got it
 * wrong; here the setter says the role on the bar, and the size is what varies
 * hold by hold.
 *
 * **A tall photo is capped and centred**, so the first fields are on screen with
 * it and the page does not read as finished the moment a picture is in — see
 * `photo-height.ts`. The cap goes on this element rather than on the canvas
 * inside it because the overlay buttons are positioned against *this* box: left
 * full width, they would float in the empty gutter beside a narrowed picture.
 *
 * **A wide photo on a phone can be opened out** the other way — see `expansion`.
 */
export function PhotoClimbSurface({
  photo,
  holdEditor,
  lineEditor,
  drawsLine,
  allowCorridor,
  missing,
  onChangePhoto,
  scanned = false,
  circuit,
  expansion,
  liveScan,
  holdFilter,
  controlsTarget = null,
}: PhotoClimbSurfaceProps) {
  const { t } = useTranslation();
  const expanded = !!expansion?.expanded;
  // Opened out, the block fills what the page's column leaves it and the frame
  // fills the block; otherwise a tall photo is capped and centred — less, for
  // the holds, the room their bar takes under it.
  const blockProps = expanded
    ? { className: 'relative mx-auto min-h-0 w-full flex-1' }
    : {
        className: 'relative mx-auto',
        style: editorPhotoFrameStyle(
          photo.naturalWidth,
          photo.naturalHeight,
          drawsLine ? undefined : HOLD_BAR_ROOM
        ),
      };
  const canvasClassName = expanded ? 'h-full' : undefined;
  const [showHelp, setShowHelp] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const circuitPanelOpen = !!circuit?.on && circuit.panelOpen;
  const overlay = liveScan ? (
    <LiveScanOverlay state={liveScan.state} onSkip={liveScan.onSkip} />
  ) : null;

  /**
   * The hand the circle in hand is taken with, and the swap, for the bar — on a
   * circuit that forces hands. Read off the page's run (`circuit.holds`), which
   * is the one with the hands on. Only one circle at a time can be swapped: a
   * swap is a fact about one move.
   *
   * The first real move has no swap of its own — its hand *is* the starting
   * hand — so there the press flips the run from the start, as the badge on the
   * list does. Everywhere else it is a swap against the alternation, written
   * onto the circle and worked out again from there (see circuit.ts).
   */
  const handControl = ((): HoldHandControl | undefined => {
    if (!circuit?.on || !circuit.forcedHands) return undefined;

    const at = holdEditor.onlySelected;
    const hold = at === null ? undefined : circuit.holds[at];
    return {
      hands: hold ? photoHandsOf(hold) : [],
      onSwap: () => {
        if (at === null || !hold) return;
        if (forcedHandIndex(circuit.holds) === at) {
          return circuit.onStartingHandChange(otherHand(hold.hand ?? 'left'));
        }
        circuit.onHoldsChange(swapPhotoHand(circuit.holds, at));
      },
    };
  })();

  // The roles: what the circle in hand is, or what the next one will be —
  // under the picture rather than on it, so no placeholder is ever under
  // the bar that would take it (#320). Still part of the drawing surface:
  // a press here acts on the mark in hand rather than putting it down.
  // Away while the circuit's list is open, which has the run's order and
  // its own way of picking a circle up, and while the photo is still
  // being read.
  // Beside the picture instead, in the column, on a phone held sideways.
  const holdBar = !circuitPanelOpen && !liveScan && (
    <div
      className={
        controlsTarget
          ? 'flex w-full flex-col items-center gap-3'
          : 'flex shrink-0 flex-col items-center gap-3 px-4 pt-3'
      }
      data-testid="climb-hold-bar"
      {...{ [DRAWING_SURFACE_ATTRIBUTE]: '' }}
    >
      <PhotoHoldBar
        editor={holdEditor}
        hand={handControl}
        docked={false}
        filter={
          holdFilter
            ? {
                open: filterOpen,
                active: holdFilter.hidden > 0,
                onToggle: () => setFilterOpen((open) => !open),
              }
            : undefined
        }
      />
      {holdFilter && filterOpen && <HoldFilterPanel {...holdFilter} />}
    </div>
  );

  /** The circuit's list: over the picture, or docked in the column beside it. */
  const circuitPanel =
    circuitPanelOpen && circuit
      ? (docked: boolean) => (
          <CircuitOverlayPanel
            docked={docked}
            holds={circuit.holds}
            onChange={circuit.onHoldsChange}
            roleOf={photoHoldRole}
            selected={holdEditor.selected}
            onSelect={(index) => holdEditor.toggleSelected(index, false)}
            forcedHands={circuit.forcedHands}
            onForcedHandsChange={circuit.onForcedHandsChange}
            onStartingHandChange={circuit.onStartingHandChange}
            onClose={() => circuit.onTogglePanel?.(false)}
          />
        )
      : null;

  if (drawsLine) {
    return (
      <div
        {...blockProps}
        data-testid="climb-photo-block"
        {...requirementAnchor('drawing')}
        {...{ [DRAWING_SURFACE_ATTRIBUTE]: '' }}
      >
        <PhotoLineCanvas
          editor={lineEditor}
          imageUrl={photo.url}
          testId="climb-photo-frame"
          resetZoomLabel={t('climbEditor.resetZoom')}
          className={canvasClassName}
        />
        <ClimbPhotoOverlayActions onChange={onChangePhoto} />
        {expansion?.offered && <PhotoExpandButton expansion={expansion} />}

        <PhotoOverlayTools
          undoLabel={t('climbEditor.line.undoPoint')}
          onUndo={lineEditor.undo}
          canUndo={lineEditor.points.length > 0}
          clearLabel={t('climbEditor.line.clear')}
          onClear={lineEditor.clearLine}
          canClear={lineEditor.points.length > 0}
          helpLabel={t('climbEditor.line.help')}
          onHelp={() => setShowHelp(true)}
        />

        <LinePopover
          editor={lineEditor}
          allowCorridor={allowCorridor}
          labels={{
            thickness: t('climbEditor.line.thickness'),
            corridor: t('climbEditor.line.corridor'),
            deletePoint: t('climbEditor.line.deletePoint'),
          }}
        />

        <MissingHint items={missing} advice />
        {overlay}

        <Dialog open={showHelp} onOpenChange={setShowHelp}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>{t('climbEditor.line.helpTitle')}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">{t('climbEditor.line.hint')}</p>
            {allowCorridor && (
              <p className="text-sm text-muted-foreground">{t('climbEditor.line.corridorHint')}</p>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <>
      <div
        {...blockProps}
        data-testid="climb-photo-block"
        {...requirementAnchor('drawing')}
        {...{ [DRAWING_SURFACE_ATTRIBUTE]: '' }}
      >
        <PhotoHoldCanvas
          editor={holdEditor}
          imageUrl={photo.url}
          testId="climb-photo-frame"
          className={canvasClassName}
          holdLabel={({ role, x, y }) =>
            t('climbEditor.holdAt', { role: t(`climbs.holdRoles.${role}.label`), x, y })
          }
          resetZoomLabel={t('climbEditor.resetZoom')}
          // The hands the circuit forces, painted onto the circles as they are
          // drawn: read off the page's own reading of the run, which is the one
          // with the hands on (see PhotoCircuitControls#holds).
          handsOf={(index) => {
            const hold = circuit?.holds[index];
            return hold ? photoHandsOf(hold) : [];
          }}
          spotlight={scanned}
        />
        <ClimbPhotoOverlayActions onChange={onChangePhoto} circuit={circuit} />
        {expansion?.offered && <PhotoExpandButton expansion={expansion} />}

        <PhotoOverlayTools
          undoLabel={t('climbEditor.holds.undo')}
          onUndo={holdEditor.undo}
          canUndo={holdEditor.canUndo}
          clearLabel={t('climbEditor.holds.clear')}
          onClear={holdEditor.clearAll}
          canClear={holdEditor.holds.length > 0}
          helpLabel={t('climbs.holdRoles.legend.open')}
          onHelp={() => setShowHelp(true)}
        />

        {/* The circuit in the order it is climbed, over the wall it runs up —
            or beside it, in the column. */}
        {circuitPanel && !controlsTarget && circuitPanel(false)}

        <MissingHint items={missing} advice />
        {overlay}

        <HoldRolesLegendDialog
          open={showHelp}
          onOpenChange={setShowHelp}
          roles={PHOTO_HOLD_ROLE_CYCLE}
          hint={t(scanned ? 'climbEditor.holds.scanHint' : 'climbEditor.holds.hint')}
        />
      </div>

      {!controlsTarget && holdBar}
      {controlsTarget &&
        createPortal(
          <>
            {holdBar}
            {circuitPanel?.(true)}
          </>,
          controlsTarget
        )}
    </>
  );
}
